/**
 * The pure half of checkout: turn a cart into priced lines and totals.
 *
 * Extracted from checkout.service.ts so the browser POS can compute a sale's
 * totals locally — against its IndexedDB catalog cache — and hand the cashier a
 * receipt without waiting on the network, while the server re-derives exactly the
 * same numbers from the database when the queued sale is pushed. One
 * implementation, so the two can't drift.
 *
 * No I/O, no clock, no ids. Stock is *reported* (`availableQty` per line), never
 * enforced: the till refuses to oversell what it can see, but a queued sale that
 * loses a race to another terminal is still accepted by the cloud and flagged —
 * so the two callers need different policies over the same arithmetic.
 */
import type { CheckoutInput, ItemCondition } from '../../shared/types'
import { AppError } from '../errors'

/** The product fields pricing needs. `Product` from shared/types satisfies this. */
export interface CalcProduct {
  id: string
  name: string
  sellingPriceCents: number
  costPriceCents: number
  quantity: number
  isActive: boolean
  hasVariants: boolean
  requiresSerial: boolean
}

/** The variant fields pricing needs. `ProductVariant` from shared/types satisfies this. */
export interface CalcVariant {
  id: string
  sellingPriceCents: number
  costPriceCents: number
  quantity: number
  isActive: boolean
}

export interface CatalogLookup {
  product(productId: string): CalcProduct | undefined
  /**
   * Resolve a variant, returning undefined unless it belongs to `productId`.
   * Ownership lives here because both callers already know it for free: the
   * server compares `product_id` on the row it just read, and the browser cache
   * stores variants grouped under their product.
   */
  variant(productId: string, variantId: string): CalcVariant | undefined
}

/** The store's VAT position, from store_config. Shared by the server and the browser till. */
export interface TaxConfig {
  registered: boolean
  /** Basis points: 750 = 7.5%. */
  rateBp: number
  /** True: catalog prices already include VAT. False: VAT is added on top. */
  inclusive: boolean
}

export const NO_TAX: TaxConfig = { registered: false, rateBp: 0, inclusive: true }

/**
 * VAT for an amount, in cents, rounded half-up to the cent.
 * Inclusive: the VAT inside `amountCents` = amount − amount / (1 + rate).
 * Exclusive: the VAT on top of `amountCents` = amount × rate.
 */
export function vatOf(amountCents: number, tax: TaxConfig): number {
  if (!tax.registered || tax.rateBp <= 0 || amountCents <= 0) return 0
  if (tax.inclusive) {
    return amountCents - Math.round((amountCents * 10_000) / (10_000 + tax.rateBp))
  }
  return Math.round((amountCents * tax.rateBp) / 10_000)
}

export interface PricedLine {
  productId: string
  productName: string
  variantId: string | null
  quantity: number
  /** What the customer is charged per unit — the override when there is one. */
  priceCents: number
  /** The catalog price, kept even when overridden so the audit log can show both. */
  listPriceCents: number
  /** Landed cost snapshotted at sale time. */
  costCents: number
  overridden: boolean
  serials: string[]
  conditions: (ItemCondition | null)[]
  warrantyMonths: number | null
  lineNote: string | null
  /** Stock the catalog believed was on hand. Callers decide what to do about it. */
  availableQty: number
}

export interface PricedCart {
  lines: PricedLine[]
  subtotalCents: number
  cogsCents: number
  discountCents: number
  /** VAT portion of totalCents, 0 when not registered. */
  vatCents: number
  totalCents: number
  /** True when any line carries a price override — these need supervisor approval. */
  hasOverride: boolean
}

export interface StockShortfall {
  productId: string
  productName: string
  variantId: string | null
  requested: number
  available: number
}

/**
 * Validate and price a cart. Throws AppError for anything that is wrong about the
 * *cart itself* (unknown product, missing variant, bad discount, missing serials);
 * insufficient stock is deliberately NOT one of those — see `stockShortfalls`.
 */
export function priceCart(input: CheckoutInput, catalog: CatalogLookup, tax: TaxConfig = NO_TAX): PricedCart {
  if (input.items.length === 0) throw new AppError('BAD_REQUEST', 'Cart is empty')

  // Two lines for the same product+variant would each be priced against the full
  // available quantity, so the pair could sell stock that doesn't exist.
  const seen = new Set<string>()
  for (const item of input.items) {
    const key = `${item.productId}|${item.variantId ?? ''}`
    if (seen.has(key)) throw new AppError('BAD_REQUEST', 'Duplicate product lines in cart')
    seen.add(key)
  }

  const lines: PricedLine[] = []
  let subtotalCents = 0
  let cogsCents = 0
  let hasOverride = false

  for (const item of input.items) {
    const p = catalog.product(item.productId)
    if (!p || !p.isActive) throw new AppError('NOT_FOUND', 'Product not found')

    let listPriceCents: number
    let costCents: number
    let availableQty: number
    let variantId: string | null = null

    if (p.hasVariants) {
      if (!item.variantId) throw new AppError('BAD_REQUEST', `${p.name}: pick a variant`)
      const v = catalog.variant(p.id, item.variantId)
      if (!v || !v.isActive) throw new AppError('NOT_FOUND', 'Variant not found')
      variantId = v.id
      listPriceCents = v.sellingPriceCents
      costCents = v.costPriceCents
      availableQty = v.quantity
    } else {
      if (item.variantId) throw new AppError('BAD_REQUEST', `${p.name} has no variants`)
      listPriceCents = p.sellingPriceCents
      costCents = p.costPriceCents
      availableQty = p.quantity
    }

    const overridden = item.priceOverrideCents != null
    const priceCents = item.priceOverrideCents ?? listPriceCents
    if (priceCents < 0) throw new AppError('BAD_REQUEST', 'Price cannot be negative')
    if (overridden) hasOverride = true

    // Serial/IMEI capture: flagged products need exactly one identifier per unit.
    // Format (IMEI Luhn) is only warned about client-side — never block a sale here.
    const serials = (item.serials ?? []).map((s) => s.trim()).filter(Boolean)
    if (p.requiresSerial && serials.length !== item.quantity) {
      throw new AppError(
        'BAD_REQUEST',
        `${p.name}: enter ${item.quantity} serial number${item.quantity === 1 ? '' : 's'}`
      )
    }
    const conditions = serials.map((_, i) => item.conditions?.[i] ?? null)

    subtotalCents += priceCents * item.quantity
    cogsCents += costCents * item.quantity
    lines.push({
      productId: p.id,
      productName: p.name,
      variantId,
      quantity: item.quantity,
      priceCents,
      listPriceCents,
      costCents,
      overridden,
      serials,
      conditions,
      warrantyMonths: item.warrantyMonths ?? null,
      lineNote: item.lineNote?.trim() || null,
      availableQty
    })
  }

  const discountCents = input.discountCents ?? 0
  if (discountCents < 0 || discountCents > subtotalCents) {
    throw new AppError('BAD_REQUEST', 'Discount cannot exceed the subtotal')
  }

  const net = subtotalCents - discountCents
  const vatCents = vatOf(net, tax)
  return {
    lines,
    subtotalCents,
    cogsCents,
    discountCents,
    vatCents,
    totalCents: tax.inclusive ? net : net + vatCents,
    hasOverride
  }
}

/** Lines whose quantity exceeds what the catalog says is on hand. */
export function stockShortfalls(lines: PricedLine[]): StockShortfall[] {
  return lines
    .filter((l) => l.availableQty < l.quantity)
    .map((l) => ({
      productId: l.productId,
      productName: l.productName,
      variantId: l.variantId,
      requested: l.quantity,
      available: l.availableQty
    }))
}

/**
 * The strict policy: refuse the sale if anything is short. Used by the normal
 * synchronous checkout and by the browser till before it queues a sale — "the
 * cloud always accepts" applies to a *replay* that lost a race, not to a cashier
 * overselling stock the screen in front of them already shows as gone.
 */
export function assertStock(lines: PricedLine[]): void {
  const short = stockShortfalls(lines)
  if (short.length > 0) {
    const s = short[0]
    throw new AppError('INSUFFICIENT_STOCK', `${s.productName}: only ${s.available} in stock`)
  }
}

/** Human-readable summary of a shortfall set, for the needs-attention list. */
export function describeShortfalls(short: StockShortfall[]): string {
  return short
    .map((s) => `${s.productName}: sold ${s.requested}, ${s.available} on hand`)
    .join('; ')
}
